import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
    ArrowUpRight,
    ArrowDown,
    UtensilsCrossed,
    Zap,
    Droplets,
    Recycle,
    ShoppingBag,
    BarChart3,
    Users,
    Megaphone,
    TrendingDown,
    Leaf,
    Globe2,
    HandHeart,
    Briefcase,
    ShieldCheck,
    ArrowRight,
    Landmark,
    Store,
    TreePine,
    Hotel,
    CheckCircle2,
} from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, MaskedLines, ChapterHeading, MarqueeStrip, Eyebrow } from "../components/primitives";
import { CTABand } from "../components/CTABand";
import { FoundingSection } from "../components/FoundingSection";
import { SupportSection } from "../components/SupportSection";
import { Newsletter } from "../components/Newsletter";
import { ReviewsPreview } from "../components/ReviewsPreview";
import { IMAGES, LOGO_FULL, API } from "../lib/site";

const CHALLENGES = [
    { icon: UtensilsCrossed, label: "Food waste" },
    { icon: Zap, label: "High energy consumption" },
    { icon: Droplets, label: "Water use" },
    { icon: Recycle, label: "Waste generation" },
    { icon: ShoppingBag, label: "Unsustainable purchasing" },
    { icon: BarChart3, label: "Limited sustainability measurement" },
    { icon: Users, label: "Employee participation challenges" },
    { icon: Megaphone, label: "Difficulty communicating credible sustainability efforts" },
    { icon: TrendingDown, label: "Pressure to remain financially competitive" },
];

const PILLARS = [
    { icon: Globe2, name: "Environment" },
    { icon: UtensilsCrossed, name: "Food" },
    { icon: Users, name: "People" },
    { icon: HandHeart, name: "Community" },
    { icon: Briefcase, name: "Business" },
    { icon: ShieldCheck, name: "Governance" },
];

const FLOW = ["Measure", "Understand", "Improve", "Save", "Report", "Scale"];

const TRAVEL_POINTS = [
    {
        icon: Landmark,
        title: "Respect Local Culture",
        desc: "Learn about and respect the people, traditions, and communities of the places you visit.",
    },
    {
        icon: Store,
        title: "Support Local",
        desc: "Support local food, businesses, producers, and community-based experiences.",
    },
    {
        icon: TreePine,
        title: "Protect Nature",
        desc: "Travel responsibly and help protect the natural environments that make destinations special.",
    },
    {
        icon: Hotel,
        title: "Choose Responsible Hospitality",
        desc: "Consider hotels and hospitality businesses that are working to improve their environmental and community impact.",
    },
];

const MARQUEE_ITEMS = [
    "Sustainable Hospitality",
    "Better Planet",
    "Stronger Business",
    "Regenerative Hospitality",
    "Food-Waste Reduction",
    "Community Impact",
    "Measurable Progress",
    "Responsible Tourism",
];

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="grain relative flex min-h-screen items-center overflow-hidden bg-[#04150D]"
        >
            <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
                <img
                    src={IMAGES.hero}
                    alt="A hotel retreat set into a lush green hillside, surrounded by nature"
                    className="h-full w-full object-cover"
                    fetchpriority="high"
                />
            </motion.div>
            <div
                aria-hidden="true"
                className="absolute inset-0 bg-gradient-to-b from-[#04150D]/90 via-[#04150D]/55 to-[#04150D]"
            />
            <div
                aria-hidden="true"
                className="absolute inset-0 bg-[radial-gradient(55%_55%_at_22%_45%,rgba(4,21,13,0.55),transparent_70%)]"
            />

            <motion.div
                style={{ opacity: fade }}
                className="relative z-[2] mx-auto w-full max-w-[1440px] px-5 pb-28 pt-36 sm:px-8 lg:px-12"
            >
                <motion.img
                    src={LOGO_FULL}
                    alt="Global Green Hotel — Sustainable Hospitality, Better Planet, Stronger Business"
                    data-testid="hero-logo"
                    className="w-60 max-w-full drop-shadow-[0_0_44px_rgba(194,161,96,0.30)] sm:w-80"
                    initial={{ opacity: 0, scale: 0.94, y: 18 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                />

                <MaskedLines
                    data-testid="hero-headline"
                    delay={0.55}
                    className="font-display mt-10 max-w-4xl text-4xl font-medium leading-[1.06] tracking-tight text-[#FDFBF7] sm:text-5xl lg:text-6xl"
                    lines={[
                        <>
                            Building a <em className="italic text-[#D9B96A]">Greener,</em>
                        </>,
                        <>
                            Stronger, <em className="italic text-[#D9B96A]">Regenerative</em>
                        </>,
                        "Hospitality Industry",
                    ]}
                />

                <Reveal delay={1.15}>
                    <p
                        data-testid="hero-subtext"
                        className="mt-7 max-w-xl text-base leading-relaxed text-[#FDFBF7]/75 md:text-lg"
                    >
                        Global Green Hotel helps hotels and hospitality businesses turn
                        sustainability into practical environmental action, stronger communities
                        and better business performance.
                    </p>
                </Reveal>

                <Reveal delay={1.3}>
                    <div className="mt-10 flex flex-wrap gap-4">
                        <Link to="/partners" data-testid="hero-cta-partner" className="btn-gold">
                            Partner With GGH
                            <ArrowUpRight size={16} aria-hidden="true" />
                        </Link>
                        <Link to="/framework" data-testid="hero-cta-approach" className="btn-outline-light">
                            Explore Our Approach
                        </Link>
                    </div>
                </Reveal>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                className="absolute bottom-8 left-1/2 z-[2] -translate-x-1/2"
                aria-hidden="true"
            >
                <motion.div
                    animate={{ y: [0, 8, 0] }}
                    transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-[#FDFBF7]/20 text-[#C2A160]"
                >
                    <ArrowDown size={18} />
                </motion.div>
            </motion.div>
        </section>
    );
};

const PLEDGED_KEY = "ggh_traveler_pledged";

const TravelerPledge = () => {
    const [count, setCount] = useState(null);
    const [pledged, setPledged] = useState(() => localStorage.getItem(PLEDGED_KEY) === "1");
    const [busy, setBusy] = useState(false);
    const [email, setEmail] = useState("");
    const [nlStatus, setNlStatus] = useState("idle");
    const [nlError, setNlError] = useState("");

    useEffect(() => {
        axios
            .get(`${API}/pledge/count`)
            .then((res) => setCount(res.data.count ?? 0))
            .catch(() => {});
    }, []);

    const takePledge = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/pledge`);
            if (typeof data.count === "number") setCount(data.count);
            localStorage.setItem(PLEDGED_KEY, "1");
            setPledged(true);
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const subscribe = async (e) => {
        e.preventDefault();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setNlError("Please enter a valid email address.");
            return;
        }
        setNlError("");
        setNlStatus("submitting");
        try {
            await axios.post(`${API}/newsletter`, { email });
            setNlStatus("success");
        } catch {
            setNlStatus("idle");
            toast.error("Could not subscribe you right now. Please try again.");
        }
    };

    const counterText =
        count === null
            ? null
            : count === 0
              ? "Be among the first travelers to take the GGH Traveler Pledge."
              : `${count} traveler${count === 1 ? " has" : "s have"} taken the GGH Traveler Pledge`;

    return (
        <section id="pledge" data-testid="pledge-section" className="scroll-mt-24 bg-[#F2EFE7] py-20 sm:py-24">
            <div className="mx-auto max-w-3xl px-5 sm:px-8">
                <Reveal>
                    <div className="grain relative overflow-hidden rounded-2xl bg-[#04150D] p-8 text-center sm:p-12">
                        <div
                            aria-hidden="true"
                            className="absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(194,161,96,0.10),transparent_65%)]"
                        />
                        <div className="relative z-[2]">
                            <Eyebrow dark>Traveler Pledge</Eyebrow>
                            <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#FDFBF7] sm:text-4xl">
                                Take the GGH Traveler Pledge 🌍
                            </h2>
                            <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-[#FDFBF7]/70 md:text-base">
                                Small choices can make a meaningful difference when we travel. Join
                                travelers who believe tourism should respect people, culture,
                                communities, and the planet.
                            </p>

                            <AnimatePresence mode="wait">
                                {!pledged ? (
                                    <motion.div
                                        key="pledge"
                                        initial={{ opacity: 0, y: 16 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -12 }}
                                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                    >
                                        <blockquote className="mx-auto mt-8 max-w-xl rounded-xl border border-[#C2A160]/30 bg-[#C2A160]/5 p-6">
                                            <p className="font-display text-lg italic leading-relaxed text-[#FDFBF7]/90">
                                                “I choose to travel with respect—for people,
                                                culture, communities, and the planet. I will support
                                                local where possible, reduce unnecessary waste,
                                                respect nature, and make more responsible travel
                                                choices.”
                                            </p>
                                        </blockquote>
                                        <button
                                            type="button"
                                            data-testid="pledge-button"
                                            onClick={takePledge}
                                            disabled={busy}
                                            className="btn-gold mt-8 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {busy ? "One moment…" : "I Take the Pledge"}
                                        </button>
                                        {counterText && (
                                            <p data-testid="pledge-count" className="mt-5 text-xs font-bold uppercase tracking-[0.18em] text-[#C2A160]">
                                                {counterText}
                                            </p>
                                        )}
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="done"
                                        data-testid="pledge-confirmation"
                                        initial={{ opacity: 0, y: 16 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                    >
                                        <CheckCircle2 size={36} className="mx-auto mt-8 text-[#D9B96A]" aria-hidden="true" />
                                        <p className="font-display mt-5 text-2xl text-[#FDFBF7]">
                                            Thank you for taking the GGH Traveler Pledge! 🌿
                                        </p>
                                        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-[#FDFBF7]/70">
                                            Every responsible choice helps build a more sustainable
                                            and regenerative future for travel and hospitality.
                                        </p>
                                        {counterText && (
                                            <p data-testid="pledge-count" className="mt-5 text-xs font-bold uppercase tracking-[0.18em] text-[#C2A160]">
                                                {counterText}
                                            </p>
                                        )}

                                        <div className="mx-auto mt-9 max-w-md border-t border-white/10 pt-8">
                                            <h3 className="font-display text-xl font-medium text-[#FDFBF7]">
                                                Stay Connected with GGH
                                            </h3>
                                            <p className="mt-2 text-xs leading-relaxed text-[#FDFBF7]/60">
                                                Receive occasional responsible travel ideas,
                                                sustainability stories, GGH updates, and ways to
                                                create positive impact when you travel.
                                            </p>
                                            {nlStatus === "success" ? (
                                                <p
                                                    data-testid="pledge-newsletter-success"
                                                    role="status"
                                                    className="mt-5 rounded-xl border border-[#C2A160]/40 bg-[#C2A160]/10 p-4 text-sm font-semibold text-[#D9B96A]"
                                                >
                                                    You're part of the GGH community — welcome!
                                                </p>
                                            ) : (
                                                <form onSubmit={subscribe} noValidate data-testid="pledge-newsletter-form" className="mt-5">
                                                    <label htmlFor="pledge-email" className="sr-only">
                                                        Email Address
                                                    </label>
                                                    <div className="flex flex-col gap-3 sm:flex-row">
                                                        <input
                                                            id="pledge-email"
                                                            data-testid="pledge-newsletter-email"
                                                            type="email"
                                                            value={email}
                                                            onChange={(e) => {
                                                                setEmail(e.target.value);
                                                                setNlError("");
                                                            }}
                                                            aria-invalid={!!nlError}
                                                            className="input-field flex-1"
                                                            placeholder="Email Address"
                                                        />
                                                        <button
                                                            type="submit"
                                                            data-testid="pledge-newsletter-submit"
                                                            disabled={nlStatus === "submitting"}
                                                            className="btn-gold disabled:cursor-not-allowed disabled:opacity-60"
                                                        >
                                                            {nlStatus === "submitting" ? "Joining…" : "Join the GGH Community"}
                                                        </button>
                                                    </div>
                                                    {nlError && (
                                                        <p className="mt-2 text-left text-xs font-semibold text-[#E8A0A0]" role="alert">
                                                            {nlError}
                                                        </p>
                                                    )}
                                                </form>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

const Home = () => {
    const { hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const t = setTimeout(() => {
                document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 500);
            return () => clearTimeout(t);
        }
    }, [hash]);
    return (
    <main>
        <SEO
            title="Global Green Hotel | Sustainable Hospitality for a Better Planet"
            description="Global Green Hotel helps hotels and hospitality businesses measure, improve and communicate practical sustainability while creating environmental, community and business value."
        />

        <Hero />
        <MarqueeStrip items={MARQUEE_ITEMS} />

        {/* 01 — What is GGH */}
        <section data-testid="section-what-is-ggh" className="py-24 sm:py-32">
            <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-12">
                <div>
                    <Reveal>
                        <ChapterHeading num="01" eyebrow="What Is Global Green Hotel?" title="Hospitality, reimagined responsibly" />
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-7 text-base leading-relaxed text-[#4A5D54] md:text-lg">
                            Global Green Hotel (GGH) is a hospitality sustainability initiative
                            focused on helping hotels and hospitality businesses understand,
                            improve and communicate their sustainability performance.
                        </p>
                        <p className="mt-5 text-base leading-relaxed text-[#4A5D54] md:text-lg">
                            GGH brings together sustainability, hospitality operations, research
                            and business thinking to encourage practical solutions that create
                            value for the environment, people, communities and hotel businesses.
                        </p>
                    </Reveal>
                    <Reveal delay={0.28}>
                        <Link to="/about" data-testid="cta-learn-about-ggh" className="btn-green mt-9">
                            Learn About GGH
                            <ArrowUpRight size={16} aria-hidden="true" />
                        </Link>
                    </Reveal>
                </div>
                <Reveal delay={0.2} className="relative">
                    <div className="relative overflow-hidden rounded-2xl border border-[#E8E6E1] shadow-[0_30px_80px_rgba(10,23,17,0.14)]">
                        <img
                            src={IMAGES.interior}
                            alt="A modern hotel interior with a living green wall of plants"
                            className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                            loading="lazy"
                        />
                        <div
                            aria-hidden="true"
                            className="absolute inset-0 bg-[radial-gradient(80%_60%_at_50%_0%,rgba(253,251,247,0.18),transparent_60%)]"
                        />
                    </div>
                    <div
                        aria-hidden="true"
                        className="absolute -bottom-5 -left-5 -z-[1] h-full w-full rounded-2xl border border-[#C2A160]/40"
                    />
                </Reveal>
            </div>
        </section>

        {/* Founder welcome */}
        <section data-testid="section-founder" className="border-y border-[#E8E6E1] bg-white py-20 sm:py-24">
            <div className="mx-auto grid max-w-[1440px] items-center gap-10 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-12">
                <Reveal className="lg:col-span-3">
                    <div className="relative mx-auto max-w-[240px] lg:mx-0">
                        <img
                            src="/assets/oliver-ouma.webp"
                            alt="Portrait of Oliver Ouma, founder of Global Green Hotel"
                            className="aspect-square w-full rounded-2xl border border-[#E8E6E1] object-cover shadow-[0_20px_50px_rgba(10,23,17,0.12)]"
                            loading="lazy"
                        />
                        <div aria-hidden="true" className="absolute -bottom-4 -right-4 -z-[1] h-full w-full rounded-2xl border border-[#C2A160]/40" />
                    </div>
                </Reveal>
                <div className="lg:col-span-9">
                    <Reveal delay={0.1}>
                        <Eyebrow>A Note From Our Founder</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <blockquote
                            data-testid="founder-welcome"
                            className="font-display mt-5 max-w-3xl text-2xl font-medium leading-snug tracking-tight text-[#0A1711] sm:text-3xl"
                        >
                            “Welcome to Global Green Hotel. I started GGH because I believe hotels
                            can protect the environment while becoming stronger, more efficient
                            businesses — and I would be glad to explore what that could look like
                            for you.”
                        </blockquote>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <p className="mt-6 text-sm font-bold text-[#0A1711]">Oliver Ouma</p>
                        <p className="mt-1 text-xs font-extrabold uppercase tracking-[0.22em] text-[#A4803F]">
                            Founder, Global Green Hotel · Sustainable Tourism & Hospitality Management
                        </p>
                        <Link
                            to="/about"
                            data-testid="cta-meet-founder"
                            className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#1A3B2B] transition-colors hover:text-[#A4803F]"
                        >
                            Learn more about GGH's story
                            <ArrowUpRight size={15} aria-hidden="true" />
                        </Link>
                    </Reveal>
                </div>
            </div>
        </section>

        {/* Organization Profile teaser */}
        <section data-testid="section-profile-teaser" className="py-16 sm:py-20">
            <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <Reveal>
                    <div className="grain relative grid items-center gap-10 overflow-hidden rounded-2xl bg-[#04150D] p-8 sm:p-12 lg:grid-cols-12">
                        <div className="relative z-[2] lg:col-span-8">
                            <Eyebrow dark>GGH Organization Profile</Eyebrow>
                            <h2 className="font-display mt-4 max-w-xl text-2xl font-medium tracking-tight text-[#FDFBF7] sm:text-3xl">
                                The complete introduction to Global Green Hotel
                            </h2>
                            <p className="mt-4 max-w-xl text-sm leading-relaxed text-[#FDFBF7]/70 md:text-base">
                                What GGH is, why it exists, how it creates value, and what makes it
                                different — including the GGH at a Glance summary and the
                                value-creation cycle.
                            </p>
                            <div className="mt-7 flex flex-wrap gap-4">
                                <Link to="/research/organization-profile" data-testid="cta-profile-read" className="btn-gold">
                                    Read the Profile
                                    <ArrowUpRight size={16} aria-hidden="true" />
                                </Link>
                                <a
                                    href="/assets/ggh-organization-profile.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="cta-profile-pdf"
                                    className="btn-outline-light"
                                >
                                    Download PDF
                                </a>
                            </div>
                        </div>
                        <div className="relative z-[2] hidden lg:col-span-4 lg:block">
                            <img
                                src={LOGO_FULL}
                                alt=""
                                aria-hidden="true"
                                className="ml-auto w-64 opacity-90 drop-shadow-[0_0_36px_rgba(194,161,96,0.25)]"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>

        {/* 02 — Why GGH */}
        <section data-testid="section-why-ggh" className="bg-[#F2EFE7] py-24 sm:py-32">
            <div className="mx-auto grid max-w-[1440px] gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-12">
                <div className="lg:col-span-5">
                    <div className="lg:sticky lg:top-32">
                        <Reveal>
                            <ChapterHeading num="02" eyebrow="Why Global Green Hotel?" title="An industry that depends on the planet" />
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-7 text-base leading-relaxed text-[#4A5D54] md:text-lg">
                                Hospitality depends heavily on natural resources, food, energy,
                                water, people and local communities. That dependence brings real
                                operational challenges:
                            </p>
                        </Reveal>
                        <Reveal delay={0.25}>
                            <blockquote className="mt-9 border-l-2 border-[#C2A160] pl-6">
                                <p className="font-display text-xl italic leading-relaxed text-[#1A3B2B] md:text-2xl">
                                    Sustainability should not simply be an additional cost. Done
                                    properly, it can become part of better hotel operations,
                                    stronger communities, environmental responsibility and
                                    long-term business performance.
                                </p>
                            </blockquote>
                        </Reveal>
                    </div>
                </div>
                <ul className="grid content-start gap-3 sm:grid-cols-2 lg:col-span-7" data-testid="challenges-list">
                    {CHALLENGES.map(({ icon: Icon, label }, i) => (
                        <Reveal key={label} delay={0.06 * i} y={20}>
                            <li className="card-lift flex h-full items-center gap-4 rounded-2xl border border-[#E8E6E1] bg-white p-5">
                                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#1A3B2B]/5 text-[#1A3B2B]">
                                    <Icon size={19} aria-hidden="true" />
                                </span>
                                <span className="text-sm font-semibold leading-snug text-[#0A1711]">
                                    {label}
                                </span>
                            </li>
                        </Reveal>
                    ))}
                </ul>
            </div>
        </section>

        {/* 03 — Vision */}
        <section data-testid="section-vision" className="grain relative overflow-hidden bg-[#04150D] py-28 sm:py-40">
            <img
                src={LOGO_FULL}
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-1/2 w-[720px] max-w-none -translate-x-1/2 -translate-y-1/2 opacity-[0.05]"
            />
            <div className="relative z-[2] mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <Reveal>
                    <p className="eyebrow-gold">03 — Our Vision</p>
                </Reveal>
                <Reveal delay={0.15}>
                    <p
                        data-testid="vision-statement"
                        className="font-display mt-8 max-w-4xl text-3xl font-medium leading-[1.2] tracking-tight text-[#FDFBF7] sm:text-5xl lg:text-6xl"
                    >
                        “A greener, stronger, and more responsible{" "}
                        <em className="italic text-[#D9B96A]">global hospitality industry.</em>”
                    </p>
                </Reveal>
            </div>
        </section>

        {/* 04 — Mission */}
        <section data-testid="section-mission" className="py-24 sm:py-32">
            <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <Reveal>
                    <ChapterHeading num="04" eyebrow="Our Mission" title="Practical. Measurable. Responsible." />
                </Reveal>
                <div className="mt-10 grid gap-10 lg:grid-cols-12">
                    <Reveal delay={0.15} className="lg:col-span-7">
                        <p className="font-display text-2xl leading-relaxed tracking-tight text-[#1A3B2B] md:text-3xl">
                            “To help hotels adopt practical, measurable, and financially
                            responsible sustainability practices that benefit the environment,
                            people, communities, guests, and the hotel business.”
                        </p>
                    </Reveal>
                    <Reveal delay={0.28} className="lg:col-span-5">
                        <div className="flex h-full flex-col justify-between rounded-2xl border border-[#C2A160]/30 bg-[#FDFBF7] p-8 shadow-[0_20px_60px_rgba(10,23,17,0.07)]">
                            <Leaf size={26} className="text-[#C2A160]" aria-hidden="true" />
                            <p
                                data-testid="brand-statement"
                                className="font-display mt-6 text-xl italic leading-relaxed text-[#0A1711]"
                            >
                                “Better hospitality is possible when sustainability becomes part
                                of the way hotels operate, innovate and create value.”
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>

        {/* 05 — Framework teaser */}
        <section data-testid="section-framework-teaser" className="bg-[#F2EFE7] py-24 sm:py-32">
            <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <div className="flex flex-wrap items-end justify-between gap-8">
                    <Reveal>
                        <ChapterHeading num="05" eyebrow="The GGH Sustainable Hospitality Framework" title="Six pillars. One direction." />
                    </Reveal>
                    <Reveal delay={0.2}>
                        <Link to="/framework" data-testid="cta-explore-framework" className="btn-outline-green">
                            Explore Our Framework
                            <ArrowUpRight size={16} aria-hidden="true" />
                        </Link>
                    </Reveal>
                </div>
                <Reveal delay={0.15}>
                    <p className="font-display mt-6 max-w-2xl text-lg italic leading-relaxed text-[#1A3B2B]">
                        Rooted in regenerative hospitality — creating positive, lasting value for
                        the environment, people, communities and business.
                    </p>
                </Reveal>
                <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {PILLARS.map(({ icon: Icon, name }, i) => (
                        <Reveal key={name} delay={0.07 * i} y={22}>
                            <Link
                                to="/framework"
                                data-testid={`pillar-teaser-${name.toLowerCase()}`}
                                className="card-lift group flex items-center justify-between rounded-2xl border border-[#E8E6E1] bg-white p-6"
                            >
                                <span className="flex items-center gap-4">
                                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1A3B2B] text-[#D9B96A] transition-colors duration-300 group-hover:bg-[#274E3A]">
                                        <Icon size={20} aria-hidden="true" />
                                    </span>
                                    <span className="font-display text-xl font-medium text-[#0A1711]">{name}</span>
                                </span>
                                <ArrowRight
                                    size={18}
                                    aria-hidden="true"
                                    className="text-[#C2A160] transition-transform duration-300 group-hover:translate-x-1"
                                />
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        {/* Free Sustainability Check teaser */}
        <section data-testid="section-mini-check" className="bg-[#F2EFE7] pb-24 sm:pb-32">
            <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <Reveal>
                    <div className="grain relative grid items-center gap-10 overflow-hidden rounded-2xl bg-[#07211A] p-8 sm:p-12 lg:grid-cols-12">
                        <div className="relative z-[2] lg:col-span-8">
                            <Eyebrow dark>Free GGH Sustainability Check</Eyebrow>
                            <h2 className="font-display mt-4 max-w-xl text-2xl font-medium tracking-tight text-[#FDFBF7] sm:text-3xl">
                                How sustainable is your hotel?
                            </h2>
                            <p className="mt-4 max-w-xl text-sm leading-relaxed text-[#FDFBF7]/70 md:text-base">
                                Five quick questions — about two minutes — to see where your hotel
                                is making progress and where opportunities for improvement may
                                exist. No payment or sign-up required.
                            </p>
                            <div className="mt-7 flex flex-wrap gap-4">
                                <Link to="/sustainability-check" data-testid="cta-mini-check" className="btn-gold">
                                    Take the Free Check
                                    <ArrowUpRight size={16} aria-hidden="true" />
                                </Link>
                            </div>
                        </div>
                        <div className="relative z-[2] hidden lg:col-span-4 lg:block">
                            <ul className="grid gap-2.5" aria-hidden="true">
                                {["Food & Waste", "Energy & Water", "People", "Community & Local Impact", "Management & Measurement"].map((a) => (
                                    <li
                                        key={a}
                                        className="rounded-full border border-[#C2A160]/30 bg-[#C2A160]/5 px-4 py-2 text-xs font-bold text-[#D9B96A]"
                                    >
                                        {a}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>

        {/* 06 — Food waste focus */}
        <section data-testid="section-food-waste" className="grain relative overflow-hidden bg-[#07211A] py-24 sm:py-32">
            <div className="relative z-[2] mx-auto grid max-w-[1440px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-12">
                <Reveal className="relative order-2 lg:order-1">
                    <div className="relative overflow-hidden rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
                        <img
                            src={IMAGES.hawaii}
                            alt="The lush green ridges of the Na Pali Coast in Hawaiʻi"
                            className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                            loading="lazy"
                        />
                        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-[#04150D]/50 to-transparent" />
                    </div>
                    <div
                        aria-hidden="true"
                        className="absolute -right-5 -top-5 -z-[1] h-full w-full rounded-2xl border border-[#C2A160]/30"
                    />
                </Reveal>
                <div className="order-1 lg:order-2">
                    <Reveal>
                        <p className="eyebrow-gold">06 — Starting With Food Waste</p>
                        <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#FDFBF7] sm:text-4xl">
                            Reducing Food Waste as a Business Strategy
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 text-base leading-relaxed text-[#FDFBF7]/70 md:text-lg">
                            GGH's initial research and development focus explores how hotels can
                            reduce food waste while improving operational and financial
                            performance.
                        </p>
                        <p className="font-display mt-5 border-l-2 border-[#C2A160] pl-5 text-lg italic leading-relaxed text-[#FDFBF7]/90">
                            “Reducing Food Waste as a Business Strategy: Developing a Financially
                            Sustainable Food-Waste Management Model for Hotels in Hawaiʻi.”
                        </p>
                    </Reveal>
                    <Reveal delay={0.25}>
                        <ol className="mt-8 flex flex-wrap items-center gap-2" data-testid="food-waste-flow">
                            {FLOW.map((step, i) => (
                                <li key={step} className="flex items-center gap-2">
                                    <span className="rounded-full border border-[#C2A160]/40 bg-[#C2A160]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-[#D9B96A]">
                                        {step}
                                    </span>
                                    {i < FLOW.length - 1 && (
                                        <ArrowRight size={14} className="text-[#C2A160]/60" aria-hidden="true" />
                                    )}
                                </li>
                            ))}
                        </ol>
                    </Reveal>
                    <Reveal delay={0.35}>
                        <Link to="/research" data-testid="cta-learn-research" className="btn-gold mt-10">
                            Learn About GGH Research
                            <ArrowUpRight size={16} aria-hidden="true" />
                        </Link>
                    </Reveal>
                </div>
            </div>
        </section>

        {/* 07 — Sustainable & Regenerative Travel */}
        <section id="travel" data-testid="section-travel" className="scroll-mt-24 border-y border-[#E8E6E1] bg-white py-24 sm:py-32">
            <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-12">
                <div>
                    <Reveal>
                        <Eyebrow>Sustainable &amp; Regenerative Travel</Eyebrow>
                        <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                            Travel Better. Leave a Positive Impact.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 text-base leading-relaxed text-[#4A5D54] md:text-lg">
                            Sustainable and regenerative hospitality also includes the choices we
                            make as travelers. GGH encourages travelers to respect local culture,
                            support local communities and businesses, protect natural
                            environments, reduce unnecessary waste, and choose hospitality
                            experiences that contribute positively to the places they visit.
                        </p>
                        <p className="font-display mt-7 border-l-2 border-[#C2A160] pl-5 text-lg italic leading-relaxed text-[#1A3B2B]">
                            Explore responsibly. Support local. Leave a positive impact.
                        </p>
                    </Reveal>
                    <Reveal delay={0.28}>
                        <Link to="/research" data-testid="cta-responsible-travel" className="btn-green mt-9">
                            Explore Responsible Travel
                            <ArrowUpRight size={16} aria-hidden="true" />
                        </Link>
                    </Reveal>
                </div>
                <ul className="grid gap-4 sm:grid-cols-2" data-testid="travel-points">
                    {TRAVEL_POINTS.map(({ icon: Icon, title, desc }, i) => (
                        <Reveal key={title} delay={0.07 * i} y={22}>
                            <li
                                data-testid={`travel-point-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                className="card-lift flex h-full flex-col rounded-2xl border border-[#E8E6E1] bg-[#FDFBF7] p-6 transition-colors duration-300 hover:border-[#1A3B2B]"
                            >
                                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1A3B2B] text-[#D9B96A]">
                                    <Icon size={20} aria-hidden="true" />
                                </span>
                                <h3 className="font-display mt-5 text-xl font-medium tracking-tight text-[#0A1711]">
                                    {title}
                                </h3>
                                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-[#4A5D54]">{desc}</p>
                            </li>
                        </Reveal>
                    ))}
                </ul>
            </div>
        </section>

        <TravelerPledge />

        <ReviewsPreview />

        <Newsletter />

        <FoundingSection />

        <SupportSection />

        <CTABand
            testid="cta-band-home"
            heading="Help Build the Future of Sustainable Hospitality"
            text="GGH is building partnerships with hotels, researchers, tourism organizations, communities and businesses that believe sustainability can create both environmental and business value."
            primary={{ to: "/partners", label: "Partner With GGH" }}
            secondary={{ to: "/contact", label: "Contact Us" }}
        />
    </main>
    );
};

export default Home;
